import { useState, useEffect } from 'react'
import api from '../../utils/axiosConfig'
import { MaleIcon, FemaleIcon } from '../../components/common/GenderIcon'

const StateTournamentRecord = () => {
  const [loading, setLoading] = useState(true)
  const [tournaments, setTournaments] = useState([])
  const [availableYears, setAvailableYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const initialFormData = {
    tournament_name: '',
    venue: '',
    city: '',
    start_date: '',
    end_date: '',
    total_teams_men: '',
    total_teams_women: '',
    winner_men: '',
    runner_up_men: '',
    third_place_men: '',
    joint_third_place_men: '',
    winner_women: '',
    runner_up_women: '',
    third_place_women: '',
    joint_third_place_women: '',
  }

  const [formData, setFormData] = useState(initialFormData)
  const [participatedEvents, setParticipatedEvents] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(false)

  useEffect(() => {
    fetchRecords()
  }, [selectedYear])

  const fetchRecords = async () => {
    try {
      setLoading(true)
      const params = selectedYear ? { session_year: selectedYear } : {}
      const response = await api.get('/user/tournament-records', { params })
      if (response.data.success) {
        const data = response.data.data
        setTournaments(data.tournaments || [])
        setAvailableYears(data.available_years || [])
        if (!selectedYear && data.session_year) {
          setSelectedYear(data.session_year)
        }
      }
    } catch (error) {
      console.error('Error fetching tournament records:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const fetchParticipatedEvents = async () => {
    try {
      setLoadingEvents(true)
      const response = await api.get('/user/tournament-records/participated-events')
      if (response.data.success) {
        setParticipatedEvents(response.data.data || [])
      }
    } catch (error) {
      console.error('Error fetching participated events:', error)
    } finally {
      setLoadingEvents(false)
    }
  }

  const handleEventSelect = (eventId) => {
    if (!eventId) {
      setFormData(initialFormData)
      return
    }
    const event = participatedEvents.find(e => e.id === parseInt(eventId))
    if (event) {
      setFormData(prev => ({
        ...prev,
        tournament_name: event.event_name || '',
        venue: event.venue || '',
        city: event.city || '',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        total_teams_men: event.total_teams_men || '',
        total_teams_women: event.total_teams_women || '',
      }))
    }
  }

  const openAddForm = () => {
    setEditingRecord(null)
    setFormData(initialFormData)
    setShowForm(true)
    fetchParticipatedEvents()
  }

  const openEditForm = (record) => {
    setEditingRecord(record)
    setFormData({
      tournament_name: record.tournament_name || '',
      venue: record.venue || '',
      city: record.city || '',
      start_date: record.start_date || '',
      end_date: record.end_date || '',
      total_teams_men: record.total_teams_men || '',
      total_teams_women: record.total_teams_women || '',
      winner_men: record.winner_men || '',
      runner_up_men: record.runner_up_men || '',
      third_place_men: record.third_place_men || '',
      joint_third_place_men: record.joint_third_place_men || '',
      winner_women: record.winner_women || '',
      runner_up_women: record.runner_up_women || '',
      third_place_women: record.third_place_women || '',
      joint_third_place_women: record.joint_third_place_women || '',
    })
    setShowForm(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.tournament_name || !formData.start_date || !formData.end_date) {
      alert('Please enter tournament name, start date and end date')
      return
    }

    setSubmitting(true)
    try {
      if (editingRecord) {
        const response = await api.put(`/user/tournament-records/${editingRecord.id}`, formData)
        if (response.data.success) {
          alert('Record updated successfully!')
        }
      } else {
        const response = await api.post('/user/tournament-records', formData)
        if (response.data.success) {
          alert('Record added successfully!')
          if (response.data.data?.session_year) {
            setSelectedYear(response.data.data.session_year)
          }
        }
      }
      setShowForm(false)
      setEditingRecord(null)
      setFormData(initialFormData)
      fetchRecords()
    } catch (error) {
      console.error('Error saving record:', error)
      alert(error.response?.data?.message || 'Failed to save record')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/user/tournament-records/${id}`)
      if (response.data.success) {
        alert('Record deleted successfully!')
        setDeleteConfirm(null)
        fetchRecords()
      }
    } catch (error) {
      console.error('Error deleting record:', error)
      alert(error.response?.data?.message || 'Failed to delete record')
    }
  }

  if (loading && tournaments.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px', display: 'block' }}></i>
          Loading tournament records...
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>
          <i className="fas fa-trophy" style={{ color: '#c00f15', marginRight: '10px' }}></i>
          State Tournament Records
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <div style={styles.selectWrapper}>
            <span style={styles.selectLabel}>Session Year:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={styles.select}
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <button onClick={openAddForm} style={styles.addBtn}>
            <i className="fas fa-plus"></i> Add Record
          </button>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>
                <i className="fas fa-edit" style={{ marginRight: '8px' }}></i>
                {editingRecord ? 'Edit Tournament Record' : 'Add Tournament Record'}
              </h3>
              <button onClick={() => { setShowForm(false); setEditingRecord(null) }} style={styles.closeBtn}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} style={styles.modalBody}>
              {/* Tournament Info */}
              <div style={styles.formSection}>
                <h4 style={styles.formSectionTitle}>
                  <i className="fas fa-info-circle" style={{ marginRight: '6px', color: '#c00f15' }}></i>
                  Tournament Details
                </h4>
                <div style={styles.formGrid}>
                  {!editingRecord ? (
                    <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                      <label style={styles.formLabel}>Tournament Name *</label>
                      <input
                        type="text"
                        value={formData.tournament_name}
                        onChange={(e) => handleFormChange('tournament_name', e.target.value)}
                        style={styles.formInput}
                        placeholder="Enter tournament name"
                        required
                      />
                    </div>
                  ) : (
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Tournament Name *</label>
                      <input
                        type="text"
                        value={formData.tournament_name}
                        style={{ ...styles.formInput, backgroundColor: '#f3f4f6' }}
                        readOnly
                      />
                    </div>
                  )}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Venue</label>
                    <input
                      type="text"
                      value={formData.venue}
                      onChange={(e) => handleFormChange('venue', e.target.value)}
                      placeholder="Enter venue"
                      style={{ ...styles.formInput, backgroundColor: editingRecord ? '#f3f4f6' : '#fff' }}
                      readOnly={!!editingRecord}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>City</label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleFormChange('city', e.target.value)}
                      placeholder="Enter city"
                      style={{ ...styles.formInput, backgroundColor: editingRecord ? '#f3f4f6' : '#fff' }}
                      readOnly={!!editingRecord}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Start Date *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleFormChange('start_date', e.target.value)}
                      style={{ ...styles.formInput, backgroundColor: editingRecord ? '#f3f4f6' : '#fff' }}
                      readOnly={!!editingRecord}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>End Date *</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => handleFormChange('end_date', e.target.value)}
                      style={{ ...styles.formInput, backgroundColor: editingRecord ? '#f3f4f6' : '#fff' }}
                      readOnly={!!editingRecord}
                    />
                  </div>
                </div>
              </div>

              {/* Men Results */}
              <div style={styles.formSection}>
                <h4 style={styles.formSectionTitle}>
                  <MaleIcon style={{ marginRight: '6px', color: '#2563eb' }} />
                  Men Results
                </h4>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Total Teams</label>
                    <input
                      type="number"
                      value={formData.total_teams_men}
                      onChange={(e) => handleFormChange('total_teams_men', e.target.value)}
                      placeholder="0"
                      min="0"
                      style={{ ...styles.formInput, backgroundColor: editingRecord ? '#f3f4f6' : '#fff' }}
                      readOnly={!!editingRecord}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Winner</label>
                    <input
                      type="text"
                      value={formData.winner_men}
                      onChange={(e) => handleFormChange('winner_men', e.target.value)}
                      style={styles.formInput}
                      placeholder="Winner team name"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Runner Up</label>
                    <input
                      type="text"
                      value={formData.runner_up_men}
                      onChange={(e) => handleFormChange('runner_up_men', e.target.value)}
                      style={styles.formInput}
                      placeholder="Runner up team name"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>3rd Place</label>
                    <input
                      type="text"
                      value={formData.third_place_men}
                      onChange={(e) => handleFormChange('third_place_men', e.target.value)}
                      style={styles.formInput}
                      placeholder="3rd place team name"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Joint 3rd Place</label>
                    <input
                      type="text"
                      value={formData.joint_third_place_men}
                      onChange={(e) => handleFormChange('joint_third_place_men', e.target.value)}
                      style={styles.formInput}
                      placeholder="Joint 3rd place team name"
                    />
                  </div>
                </div>
              </div>

              {/* Women Results */}
              <div style={styles.formSection}>
                <h4 style={styles.formSectionTitle}>
                  <FemaleIcon style={{ marginRight: '6px', color: '#db2777' }} />
                  Women Results
                </h4>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Total Teams</label>
                    <input
                      type="number"
                      value={formData.total_teams_women}
                      onChange={(e) => handleFormChange('total_teams_women', e.target.value)}
                      placeholder="0"
                      min="0"
                      style={{ ...styles.formInput, backgroundColor: editingRecord ? '#f3f4f6' : '#fff' }}
                      readOnly={!!editingRecord}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Winner</label>
                    <input
                      type="text"
                      value={formData.winner_women}
                      onChange={(e) => handleFormChange('winner_women', e.target.value)}
                      style={styles.formInput}
                      placeholder="Winner team name"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Runner Up</label>
                    <input
                      type="text"
                      value={formData.runner_up_women}
                      onChange={(e) => handleFormChange('runner_up_women', e.target.value)}
                      style={styles.formInput}
                      placeholder="Runner up team name"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>3rd Place</label>
                    <input
                      type="text"
                      value={formData.third_place_women}
                      onChange={(e) => handleFormChange('third_place_women', e.target.value)}
                      style={styles.formInput}
                      placeholder="3rd place team name"
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Joint 3rd Place</label>
                    <input
                      type="text"
                      value={formData.joint_third_place_women}
                      onChange={(e) => handleFormChange('joint_third_place_women', e.target.value)}
                      style={styles.formInput}
                      placeholder="Joint 3rd place team name"
                    />
                  </div>
                </div>
              </div>

              <div style={styles.formActions}>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingRecord(null) }}
                  style={styles.cancelBtn}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn} disabled={submitting}>
                  {submitting ? (
                    <><i className="fas fa-spinner fa-spin"></i> Saving...</>
                  ) : (
                    <><i className="fas fa-save"></i> {editingRecord ? 'Update Record' : 'Save Record'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, maxWidth: '400px' }}>
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: '40px', color: '#f59e0b', marginBottom: '12px' }}></i>
              <h3 style={{ margin: '0 0 8px', fontSize: '18px' }}>Delete Record?</h3>
              <p style={{ color: '#666', fontSize: '14px', margin: '0 0 20px' }}>
                Are you sure you want to delete "{deleteConfirm.tournament_name}"? This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button onClick={() => setDeleteConfirm(null)} style={styles.cancelBtn}>Cancel</button>
                <button onClick={() => handleDelete(deleteConfirm.id)} style={{ ...styles.submitBtn, background: '#ef4444' }}>
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tournament List */}
      {tournaments.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>
            <i className="fas fa-trophy"></i>
          </div>
          <div style={styles.emptyText}>No tournament records found</div>
          <div style={styles.emptySubtext}>
            {selectedYear ? `No records for session ${selectedYear}` : 'No records yet'}
          </div>
          <button onClick={openAddForm} style={{ ...styles.addBtn, marginTop: '16px' }}>
            <i className="fas fa-plus"></i> Add Your First Record
          </button>
        </div>
      ) : (
        tournaments.map((record, idx) => (
          <div key={record.id || idx} style={styles.card}>
            <div style={styles.cardHeader}>
              <h3 style={styles.eventName}>{record.tournament_name}</h3>
              {record.can_edit && (
                <div style={styles.cardActions}>
                  <button onClick={() => openEditForm(record)} style={styles.editBtn} title="Edit">
                    <i className="fas fa-pen"></i>
                  </button>
                  <button onClick={() => setDeleteConfirm(record)} style={styles.deleteBtn} title="Delete">
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              )}
            </div>

            <div style={styles.eventMeta}>
              {(record.venue || record.city) && (
                <div style={styles.metaItem}>
                  <i className="fas fa-map-marker-alt" style={styles.metaIcon}></i>
                  <span>{[record.venue, record.city].filter(Boolean).join(', ')}</span>
                </div>
              )}
              {record.formatted_dates && (
                <div style={styles.metaItem}>
                  <i className="fas fa-calendar-alt" style={styles.metaIcon}></i>
                  <span>{record.formatted_dates}</span>
                </div>
              )}
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.th, ...styles.thFirst }}>Particulars</th>
                    <th style={styles.th}>Men</th>
                    <th style={styles.th}>Women</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Total Participating Teams</td>
                    <td style={styles.td}>{record.total_teams_men || 0}</td>
                    <td style={styles.td}>{record.total_teams_women || 0}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Winner</td>
                    <td style={styles.td}>{record.winner_men || '-'}</td>
                    <td style={styles.td}>{record.winner_women || '-'}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Runner Up</td>
                    <td style={styles.td}>{record.runner_up_men || '-'}</td>
                    <td style={styles.td}>{record.runner_up_women || '-'}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>3rd Place</td>
                    <td style={styles.td}>{record.third_place_men || '-'}</td>
                    <td style={styles.td}>{record.third_place_women || '-'}</td>
                  </tr>
                  <tr>
                    <td style={{ ...styles.td, ...styles.tdFirst }}>Joint 3rd Place</td>
                    <td style={styles.td}>{record.joint_third_place_men || '-'}</td>
                    <td style={styles.td}>{record.joint_third_place_women || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
    </div>
  )
}

const styles = {
  container: {
    padding: '24px',
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: 0,
  },
  selectWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  selectLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#444',
  },
  select: {
    padding: '8px 14px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    backgroundColor: '#fff',
    minWidth: '130px',
  },
  addBtn: {
    padding: '8px 16px',
    backgroundColor: '#c00f15',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  card: {
    border: '2px solid #c00f15',
    borderRadius: '10px',
    marginBottom: '28px',
    overflow: 'hidden',
    backgroundColor: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
  },
  cardHeader: {
    backgroundColor: '#c00f15',
    color: '#fff',
    padding: '14px 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  eventName: {
    fontSize: '17px',
    fontWeight: '700',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'white',
  },
  cardActions: {
    position: 'absolute',
    right: '14px',
    display: 'flex',
    gap: '6px',
  },
  editBtn: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
  },
  deleteBtn: {
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.4)',
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
  },
  eventMeta: {
    padding: '10px 20px',
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    flexWrap: 'wrap',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e9ecef',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#555',
  },
  metaIcon: {
    color: '#c00f15',
    fontSize: '14px',
  },
  tableWrapper: {
    overflowX: 'auto',
    padding: '0',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    padding: '10px 14px',
    textAlign: 'center',
    fontWeight: '700',
    fontSize: '13px',
    borderBottom: '2px solid #dee2e6',
    backgroundColor: '#fef2f2',
    color: '#1a1a2e',
  },
  thFirst: {
    textAlign: 'left',
    paddingLeft: '20px',
    width: '200px',
  },
  td: {
    padding: '9px 14px',
    textAlign: 'center',
    borderBottom: '1px solid #eee',
    color: '#333',
  },
  tdFirst: {
    textAlign: 'left',
    paddingLeft: '20px',
    fontWeight: '600',
    color: '#1a1a2e',
  },
  loading: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#888',
    fontSize: '15px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '60px 20px',
    color: '#888',
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#ddd',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '500',
  },
  emptySubtext: {
    fontSize: '13px',
    color: '#aaa',
    marginTop: '4px',
  },
  // Modal styles
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: '2px solid #f3f4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 1,
  },
  closeBtn: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    border: 'none',
    background: '#f3f4f6',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    color: '#666',
  },
  modalBody: {
    padding: '20px',
  },
  formSection: {
    marginBottom: '20px',
    padding: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#fafafa',
  },
  formSectionTitle: {
    margin: '0 0 12px',
    fontSize: '15px',
    fontWeight: '700',
    color: '#1a1a2e',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '12px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  formLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#555',
  },
  formInput: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb',
  },
  cancelBtn: {
    padding: '8px 20px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#fff',
    color: '#555',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '8px 20px',
    border: 'none',
    borderRadius: '6px',
    background: '#c00f15',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
}

export default StateTournamentRecord
